import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { adminSupabase } from '@/integrations/supabase/adminClient';
import { INDUSTRIES } from '@/data/industries';

const emptyOrganizationForm = {
  organization_name: '', owner_full_name: '', owner_email: '', industry: '',
  commercial_registration_number: '', unified_number: '', vat_number: '',
  contact_name: '', contact_number: '', contact_email: '',
};

async function edgeFunctionErrorMessage(error: unknown) {
  const context = (error as { context?: unknown } | null)?.context;
  if (context instanceof Response) {
    try {
      const payload = await context.clone().json() as { error?: unknown };
      if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
    } catch {
      // The response did not contain JSON; use the safe fallback below.
    }
  }

  if (error instanceof Error && !error.message.includes('non-2xx status code')) {
    return error.message;
  }
  return 'Unable to create the organization. Please try again.';
}

export function NewOrganizationDialog() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyOrganizationForm);

  const createOrganization = useMutation({
    mutationFn: async () => {
      if (!form.organization_name.trim() || !form.owner_full_name.trim()) {
        throw new Error('Company name and account owner name are required.');
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email.trim())) {
        throw new Error('Enter a valid account owner email.');
      }
      if (!/^\d{10}$/.test(form.commercial_registration_number)) {
        throw new Error('Commercial Registration / National ID must be exactly 10 digits.');
      }
      if (!form.industry) throw new Error('Select the organization industry.');
      if (form.unified_number && !/^7\d{9}$/.test(form.unified_number)) {
        throw new Error('Unified Number must start with 7 and be exactly 10 digits.');
      }
      if (form.vat_number && !/^3\d{14}$/.test(form.vat_number)) {
        throw new Error('VAT Number must start with 3 and be exactly 15 digits.');
      }
      if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim())) {
        throw new Error('Enter a valid contact email.');
      }

      const { data, error } = await adminSupabase.functions.invoke('provision-enterprise-customer', {
        body: form,
      });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (!data?.success) throw new Error(data?.error ?? 'Unable to create the organization.');
      return data as { invitation_status: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sa-organizations'] });
      queryClient.invalidateQueries({ queryKey: ['enterprise-deal-organizations'] });
      setForm(emptyOrganizationForm);
      setOpen(false);
      toast({
        title: 'Organization created',
        description: data.invitation_status === 'sent'
          ? 'A secure account-setup link was emailed to the organization owner.'
          : 'The existing owner account was linked and a new account-setup link was emailed.',
      });
    },
    onError: (error: Error) => toast({
      title: 'Could not create organization',
      description: error.message,
      variant: 'destructive',
    }),
  });

  return (
    <>
      <Button className="gap-2" onClick={() => setOpen(true)}>
        <Building2 className="h-4 w-4" /> New organization
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Create organization</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Company name *</Label><Input value={form.organization_name} onChange={(event) => setForm((current) => ({ ...current, organization_name: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Account owner name *</Label><Input value={form.owner_full_name} onChange={(event) => setForm((current) => ({ ...current, owner_full_name: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Account owner email *</Label><Input type="email" value={form.owner_email} onChange={(event) => setForm((current) => ({ ...current, owner_email: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Commercial Registration / National ID *</Label><Input inputMode="numeric" maxLength={10} value={form.commercial_registration_number} onChange={(event) => setForm((current) => ({ ...current, commercial_registration_number: event.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit number" /></div>
            <div className="space-y-1.5"><Label>Industry *</Label><select value={form.industry} onChange={(event) => setForm((current) => ({ ...current, industry: event.target.value }))} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"><option value="">Select industry</option>{INDUSTRIES.map((industry) => <option key={industry} value={industry}>{industry}</option>)}</select></div>
            <div className="space-y-1.5"><Label>Unified number</Label><Input inputMode="numeric" maxLength={10} value={form.unified_number} onChange={(event) => setForm((current) => ({ ...current, unified_number: event.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="700XXXXXXX" /></div>
            <div className="space-y-1.5"><Label>VAT number</Label><Input inputMode="numeric" maxLength={15} value={form.vat_number} onChange={(event) => setForm((current) => ({ ...current, vat_number: event.target.value.replace(/\D/g, '').slice(0, 15) }))} placeholder="300XXXXXXXXXXXX" /></div>
            <div className="space-y-1.5"><Label>Contact name</Label><Input value={form.contact_name} onChange={(event) => setForm((current) => ({ ...current, contact_name: event.target.value }))} placeholder="Defaults to account owner" /></div>
            <div className="space-y-1.5"><Label>Contact number</Label><Input value={form.contact_number} onChange={(event) => setForm((current) => ({ ...current, contact_number: event.target.value }))} placeholder="+966 5XXXXXXXX" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={(event) => setForm((current) => ({ ...current, contact_email: event.target.value }))} placeholder="Defaults to account owner email" /></div>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground sm:col-span-2">This creates the same company, employer profile, owner membership, billing identity, and default-plan records as normal ForkHire registration. The owner receives a secure passwordless setup link; Fork staff never create or know the password.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createOrganization.mutate()} disabled={createOrganization.isPending || !form.organization_name.trim() || !form.owner_full_name.trim() || !form.owner_email.trim() || !form.commercial_registration_number || !form.industry}>
              {createOrganization.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              Create organization and invite owner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
